import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/ui/EmptyState';
import { 
  BarChart3, 
  Clock, 
  Mail, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  TrendingUp,
  History,
  CalendarDays,
  Inbox
} from 'lucide-react';

const GET_EMAIL_STATS = gql`
  query GetEmailStats {
    getEmailStats {
      awsStatus {
        sendQuota {
          max24HourSend
          maxSendRate
          sentLast24Hours
        }
        sendingEnabled
        stats {
          deliveries
          bounces
          complaints
          rejects
        }
      }
      topSubjects {
        subject_preview
        total_count
        sent
        failed
        pending
        processing
      }
      hourlySends {
        hour
        total_sent
        successful
        failed
        pending
      }
      dailyTotals {
        date
        total_emails
        sent
        failed
      }
      queueStatus {
        pending
        processing
        sent
        failed
      }
      generatedAt
    }
  }
`;

export const EmailStatsView: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(GET_EMAIL_STATS, {
    pollInterval: 30000, // Refresh every 30 seconds
  });

  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    if (data) {
      setLastUpdate(new Date());
    }
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading email statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">Failed to load email statistics</p>
        <p className="text-gray-500 text-sm mt-2">{error.message}</p>
      </div>
    );
  }

  const stats = data?.getEmailStats;
  if (!stats) return null;

  const { awsStatus, topSubjects, hourlySends, dailyTotals, queueStatus } = stats;

  // Calculate usage percentage
  const usagePercent = (awsStatus.sendQuota.sentLast24Hours / awsStatus.sendQuota.max24HourSend) * 100;

  return (
    <div className="space-y-6">
      {/* Header with AWS Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">24hr Sent</p>
                <p className="text-2xl font-bold">{awsStatus.sendQuota.sentLast24Hours}</p>
                <p className="text-xs text-gray-500">of {awsStatus.sendQuota.max24HourSend}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Send Rate</p>
                <p className="text-2xl font-bold">{awsStatus.sendQuota.maxSendRate}/sec</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Deliveries</p>
                <p className="text-2xl font-bold">{awsStatus.stats.deliveries}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <XCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-sm font-medium text-gray-600">Issues</p>
                <p className="text-2xl font-bold">
                  {awsStatus.stats.bounces + awsStatus.stats.complaints + awsStatus.stats.rejects}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Daily Quota Usage</span>
            <Badge variant={usagePercent > 80 ? 'destructive' : usagePercent > 60 ? 'default' : 'secondary'}>
              {usagePercent.toFixed(1)}% Used
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={usagePercent} className="mb-2" />
          <p className="text-sm text-gray-600">
            {awsStatus.sendQuota.sentLast24Hours} of {awsStatus.sendQuota.max24HourSend} emails sent in the last 24 hours
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue="subjects" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subjects">Subject Analysis</TabsTrigger>
          <TabsTrigger value="hourly">Hourly Patterns</TabsTrigger>
          <TabsTrigger value="daily">Daily Trends</TabsTrigger>
          <TabsTrigger value="queue">Queue Status</TabsTrigger>
        </TabsList>

        <TabsContent value="subjects">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Top Email Subjects (Today) - Spam Detection
              </CardTitle>
              <p className="text-sm text-gray-600">
                First 8 characters of email subjects with send counts - useful for identifying spam patterns
              </p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject Preview</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topSubjects?.slice(0, 20).map((subject: any, index: number) => {
                    const successRate = (subject.sent / subject.total_count) * 100;
                    const isHighVolume = subject.total_count > 50; // Flag potential spam
                    
                    return (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-sm">
                          "{subject.subject_preview}..."
                          {isHighVolume && (
                            <Badge variant="destructive" className="ml-2 text-xs">
                              HIGH VOL
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {subject.total_count}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {subject.sent}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {subject.failed}
                        </TableCell>
                        <TableCell className="text-right text-yellow-600">
                          {subject.pending}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={successRate > 90 ? 'default' : successRate > 70 ? 'secondary' : 'destructive'}
                          >
                            {successRate.toFixed(0)}% success
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {(!topSubjects || topSubjects.length === 0) && (
                <EmptyState 
                  icon={Inbox}
                  title="No email activity today"
                  description="Subjects and send counts will appear here once activity is detected."
                  className="py-12"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hourly">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Hourly Sending Pattern (Today)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hour</TableHead>
                    <TableHead className="text-right">Total Sent</TableHead>
                    <TableHead className="text-right">Successful</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hourlySends?.map((hour: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {hour.hour}:00 - {hour.hour + 1}:00
                      </TableCell>
                      <TableCell className="text-right">{hour.total_sent}</TableCell>
                      <TableCell className="text-right text-green-600">{hour.successful}</TableCell>
                      <TableCell className="text-right text-red-600">{hour.failed}</TableCell>
                      <TableCell className="text-right text-yellow-600">{hour.pending}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(!hourlySends || hourlySends.length === 0) && (
                <EmptyState 
                  icon={History}
                  title="No hourly data available"
                  description="Hourly sending patterns will be graphed once data is collected for today."
                  className="py-12"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily">
          <Card>
            <CardHeader>
              <CardTitle>Daily Email Totals (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Total Emails</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                    <TableHead>Success Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyTotals?.map((day: any, index: number) => {
                    const successRate = (day.sent / day.total_emails) * 100;
                    return (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{day.date}</TableCell>
                        <TableCell className="text-right">{day.total_emails}</TableCell>
                        <TableCell className="text-right text-green-600">{day.sent}</TableCell>
                        <TableCell className="text-right text-red-600">{day.failed}</TableCell>
                        <TableCell>
                          <Badge variant={successRate > 90 ? 'default' : 'secondary'}>
                            {successRate.toFixed(1)}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {(!dailyTotals || dailyTotals.length === 0) && (
                <EmptyState 
                  icon={CalendarDays}
                  title="No daily trends found"
                  description="Aggregate email totals for the last 7 days will be displayed here."
                  className="py-12"
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="queue">
          <Card>
            <CardHeader>
              <CardTitle>Current Queue Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{queueStatus?.pending || 0}</p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{queueStatus?.processing || 0}</p>
                  <p className="text-sm text-gray-600">Processing</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{queueStatus?.sent || 0}</p>
                  <p className="text-sm text-gray-600">Sent</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{queueStatus?.failed || 0}</p>
                  <p className="text-sm text-gray-600">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        Last updated: {lastUpdate.toLocaleTimeString()} • Auto-refreshes every 30 seconds
      </div>
    </div>
  );
};